import type { NavigateFunction } from 'react-router-dom';

export interface DrillCrumb {
    path: string;
    label: string;
    historyIndex?: number;
}

interface RouterHistoryState {
    idx?: number;
    key?: string;
    usr?: unknown;
}

const historyIndex = (): number => {
    const state = window.history.state as RouterHistoryState | null;
    return typeof state?.idx === 'number' ? state.idx : 0;
};

export const canPopHistory = (): boolean => historyIndex() > 0;

export const stampHistoryState = (patch: Record<string, unknown>): void => {
    const state = window.history.state as RouterHistoryState | null;
    if (!state) {
        return;
    }

    const usr =
        state.usr && typeof state.usr === 'object'
            ? (state.usr as Record<string, unknown>)
            : {};

    window.history.replaceState(
        {
            ...state,
            usr: {
                ...usr,
                ...patch,
            },
        },
        '',
    );
};

export const getLastDrillCrumbLabel = (
    trail: DrillCrumb[] | undefined,
    fallback: string,
): string => {
    return trail?.[trail.length - 1]?.label ?? fallback;
};

export const withDrillTrail = <T>(
    current: { trail?: DrillCrumb[] } | undefined,
    crumb: DrillCrumb,
    next: T,
): T & { trail: DrillCrumb[] } => ({
    ...next,
    trail: [
        ...(current?.trail ?? []),
        {
            ...crumb,
            historyIndex: crumb.historyIndex ?? historyIndex(),
        },
    ],
});

export const popDrillHistory = (
    navigate: NavigateFunction,
    trail: DrillCrumb[] | undefined,
    fallback: () => void,
): void => {
    if (trail?.length && canPopHistory()) {
        navigate(-1);
        return;
    }

    fallback();
};

export const popDrillToCrumb = (
    navigate: NavigateFunction,
    crumb: DrillCrumb,
    fallback: () => void,
): void => {
    if (typeof crumb.historyIndex !== 'number') {
        fallback();
        return;
    }

    const stepsBack = historyIndex() - crumb.historyIndex;
    if (stepsBack > 0) {
        navigate(-stepsBack);
        return;
    }

    fallback();
};
